import React, { useState, useContext } from 'react';
import { AuthContext } from '../../components/AuthWrapper';
import { Navigate, Link } from 'react-router-dom';
import { migrateDataFromJSONFiles, exportDataToJSON } from '../../utils/dataService';

const DataMigration = () => {
  const { isAdmin } = useContext(AuthContext);
  const [migrating, setMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState('');
  const [exportCollection, setExportCollection] = useState('');
  const [exportData, setExportData] = useState('');
  const [exportStatus, setExportStatus] = useState('');

  // Redirect if not admin
  if (!isAdmin) {
    return <Navigate to="/" />;
  }

  const handleMigrateData = async () => {
    if (window.confirm('Are you sure you want to migrate all JSON data to Firebase? This will only import data if the collections are empty.')) {
      try {
        setMigrating(true);
        setMigrationStatus('Starting migration...');
        
        await migrateDataFromJSONFiles();
        
        setMigrationStatus('Migration completed successfully!');
      } catch (error) {
        console.error('Migration error:', error);
        setMigrationStatus(`Migration failed: ${error.message}`);
      } finally {
        setMigrating(false);
      }
    }
  };

  const handleExportData = async () => {
    if (!exportCollection) {
      setExportStatus('Please select a collection to export');
      return;
    }
    
    try {
      setExportStatus('Exporting data...');
      
      const jsonData = await exportDataToJSON(exportCollection);
      setExportData(jsonData);
      
      setExportStatus('Export completed successfully!');
    } catch (error) {
      console.error('Export error:', error);
      setExportStatus(`Export failed: ${error.message}`);
      setExportData('');
    }
  };

  const handleCopyToClipboard = () => {
    navigator.clipboard.writeText(exportData);
    setExportStatus('Copied to clipboard!');
  };

  const handleDownloadJSON = () => {
    const blob = new Blob([exportData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${exportCollection}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="container mx-auto py-8 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-navy">Data Migration</h1>
        <Link 
          to="/admin"
          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
        >
          Back to Dashboard
        </Link>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Import Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Import JSON to Firebase</h2>
          <p className="mb-4 text-gray-700">
            This will migrate all static JSON data from the <code>/data</code> directory to Firebase Firestore.
            The migration will only happen if the collections in Firestore are empty.
          </p>
          
          <button
            onClick={handleMigrateData}
            disabled={migrating}
            className={`w-full py-3 rounded font-medium ${
              migrating 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-green-600 hover:bg-green-700 text-white'
            }`}
          >
            {migrating ? 'Migrating...' : 'Start Migration'}
          </button>
          
          {migrationStatus && (
            <div className={`mt-4 p-3 rounded ${
              migrationStatus.includes('failed') 
                ? 'bg-red-100 text-red-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {migrationStatus}
            </div>
          )}
        </div>
        
        {/* Export Section */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Export Firebase Data to JSON</h2>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Select Collection</label>
            <select
              value={exportCollection}
              onChange={(e) => setExportCollection(e.target.value)}
              className="w-full p-2 border rounded"
            >
              <option value="">-- Select Collection --</option>
              <option value="timeline">Timeline</option>
              <option value="exhibits">Exhibits</option>
              <option value="audio">Audio Transcripts</option>
              <option value="legal-claims">Legal Claims</option>
              <option value="documents">Documents</option>
            </select>
          </div>
          
          <button
            onClick={handleExportData}
            disabled={!exportCollection}
            className={`w-full py-3 rounded font-medium mb-4 ${
              !exportCollection 
                ? 'bg-gray-400 cursor-not-allowed' 
                : 'bg-blue-600 hover:bg-blue-700 text-white'
            }`}
          >
            Export Data
          </button>
          
          {exportStatus && (
            <div className={`mb-4 p-3 rounded ${
              exportStatus.includes('failed') 
                ? 'bg-red-100 text-red-700' 
                : 'bg-green-100 text-green-700'
            }`}>
              {exportStatus}
            </div>
          )}
          
          {exportData && (
            <div>
              <div className="mb-4 flex space-x-2">
                <button
                  onClick={handleCopyToClipboard}
                  className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded"
                >
                  Copy to Clipboard
                </button>
                <button
                  onClick={handleDownloadJSON}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  Download JSON
                </button>
              </div>
              
              <pre className="bg-gray-100 p-4 rounded text-sm overflow-auto max-h-80">
                {exportData}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataMigration;